import {
  Dashboard as GridIcon,
  ViewList as ListIcon,
  ViewModule as ModuleIcon,
  ViewWeek as ThreeColumnsIcon,
  ViewColumn as TwoColumnsIcon,
  ViewStream as TwoRowsIcon,
} from '@mui/icons-material';
import {
  Box,
  ButtonGroup,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { memo, useCallback } from 'react';

import type { ControlProps } from './SettingControl';

export enum ELayout {
  'SINGLE' = 'SINGLE',
  'TWO_COLUMNS' = 'TWO_COLUMNS',
  'THREE_COLUMNS' = 'THREE_COLUMNS',
  'TWO_ROWS' = 'TWO_ROWS',
  'GRID_2X2' = 'GRID_2X2',
  'LIST' = 'LIST',
}

interface LayoutOption {
  value: ELayout;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const LAYOUT_OPTIONS: LayoutOption[] = [
  {
    value: ELayout.SINGLE,
    label: 'Single',
    icon: <ModuleIcon />,
    description: 'Single full-width layout',
  },
  {
    value: ELayout.TWO_COLUMNS,
    label: 'Two Columns',
    icon: <TwoColumnsIcon />,
    description: 'Two equal columns',
  },
  {
    value: ELayout.THREE_COLUMNS,
    label: 'Three Columns',
    icon: <ThreeColumnsIcon />,
    description: 'Three equal columns',
  },
  {
    value: ELayout.TWO_ROWS,
    label: 'Two Rows',
    icon: <TwoRowsIcon />,
    description: 'Two equal rows',
  },
  {
    value: ELayout.GRID_2X2,
    label: '2x2 Grid',
    icon: <GridIcon />,
    description: '2x2 grid layout',
  },
  {
    value: ELayout.LIST,
    label: 'List',
    icon: <ListIcon />,
    description: 'Vertical list layout',
  },
];

export interface LayoutControlProps extends ControlProps<ELayout> {
  onChange?: (value: ELayout) => void;
  type: 'layout';
}
export const LayoutControl = memo<Omit<LayoutControlProps, 'type'>>(
  ({ value, onChange, label = 'Layout', description }) => {
    const currentValue = value;
    const handleLayoutChange = useCallback(
      (newLayout: ELayout) => {
        onChange?.(newLayout);
      },
      [onChange],
    );

    return (
      <Box>
        <Typography gutterBottom variant="subtitle2">
          {label}
        </Typography>

        <ButtonGroup
          variant="outlined"
          size="small"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            '& .MuiButtonGroup-grouped': {
              border: '1px solid',
              borderColor: 'divider',
              '&:not(:last-of-type)': {
                borderRightColor: 'divider',
              },
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
            },
          }}
        >
          {LAYOUT_OPTIONS.map(option => (
            <Tooltip
              key={option.value}
              title={option.description}
              placement="top"
            >
              <IconButton
                size="small"
                onClick={() => handleLayoutChange(option.value)}
                className={currentValue === option.value ? 'Mui-selected' : ''}
                sx={{
                  minWidth: 40,
                  height: 40,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                }}
              >
                {option.icon}
              </IconButton>
            </Tooltip>
          ))}
        </ButtonGroup>

        {description && (
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mt: 1, display: 'block' }}
          >
            {description}
          </Typography>
        )}
      </Box>
    );
  },
);

LayoutControl.displayName = 'LayoutControl';

export { LAYOUT_OPTIONS };
export type { LayoutOption };
